import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { loginSchema } from '@/lib/validation';
import { authApi, ApiError } from '@/lib/pillarApi';
import { useAuthStore, type EffectiveSessionGeneration } from '@/store/auth';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { AnchorbaseLogo } from '@/components/branding/AnchorbaseLogo';
import { sessionSensitiveAuthApi } from '@/lib/sessionSensitiveAuthApi';
import {
    buildCloverOAuthLaunchPath,
    parseCloverOAuthLoginContext,
} from '@/lib/clover/oauth-launch-context';
import {
    SessionUserProfileDetailedResponse,
    SessionUserProfileDetailedResponseSchema,
    JsonValuePrunedSchema,
    normalizeUserEmailInputForSubmission,
} from '@anchorbase/pillar-sdk';
import { PASSWORDLESS_LOGIN_EMAIL_STORAGE_KEY } from './passwordless-login-context';

export const LoginPageFormSchema: z.ZodObject<{
    username: typeof loginSchema.shape.email;
}> = z.object({
    username: loginSchema.shape.email,
});

export type LoginPageFormData = z.infer<typeof LoginPageFormSchema>;

export default function LoginPage() {
    const log = createComponentLogger('LoginPage');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const prefilledEmail: string = normalizeUserEmailInputForSubmission(
        searchParams.get('email') ?? ''
    );
    const paymentRequestUuid: string = searchParams.get('payment_request_uuid')?.trim() ?? '';
    const returnToPayment: boolean = searchParams.get('return_to_payment') === '1';
    const cloverOAuthLaunchContext = parseCloverOAuthLoginContext(searchParams);
    const postLoginPath: string =
        cloverOAuthLaunchContext === null
            ? returnToPayment
                ? '/pay'
                : '/dashboard'
            : buildCloverOAuthLaunchPath(cloverOAuthLaunchContext);
    const { user, setUserForEffectiveSessionGeneration, getEffectiveSessionGeneration } =
        useAuthStore();
    const [error, setError] = useState<string>('');
    const [isRedirecting, setIsRedirecting] = useState(false);
    const hasAttemptedExistingSessionPaymentLinkAttach = useRef(false);
    const existingProfileGenerationRef = useRef<EffectiveSessionGeneration>(
        getEffectiveSessionGeneration()
    );
    // Check if user already has a valid session
    const { data: existingProfile, isLoading: isCheckingSession } =
        useQuery<SessionUserProfileDetailedResponse | null>({
            queryKey: ['profile'],
            queryFn: async () => {
                const response = await sessionSensitiveAuthApi.getProfile();
                if (response === null || response === undefined) {
                    return null;
                }
                existingProfileGenerationRef.current = getEffectiveSessionGeneration();
                return SessionUserProfileDetailedResponseSchema.parse(response);
            },
            enabled: !user, // Only check if user is not already in store
            retry: false,
            staleTime: 0,
            gcTime: 0,
            refetchOnWindowFocus: false,
            refetchOnMount: true,
            refetchOnReconnect: false,
        });

    // Redirect to dashboard or the payment page if user is already authenticated
    useEffect(() => {
        if (user || existingProfile) {
            if (existingProfile && !user) {
                // If we fetched profile but user is not in store, set it
                setUserForEffectiveSessionGeneration(
                    existingProfile,
                    existingProfileGenerationRef.current
                );
            }
            if (
                existingProfile?.requires_password_change === true ||
                user?.requires_password_change === true
            ) {
                runInBackground(navigate('/profile/password', { replace: true }), (err) => {
                    log.error('Error navigating to password change', err);
                });
                return;
            }
            if (
                paymentRequestUuid.length > 0 &&
                !hasAttemptedExistingSessionPaymentLinkAttach.current
            ) {
                hasAttemptedExistingSessionPaymentLinkAttach.current = true;
                if (returnToPayment) {
                    setIsRedirecting(true);
                    runInBackground(navigate('/pay', { replace: true }), (err) => {
                        setIsRedirecting(false);
                        setError(
                            err instanceof Error && err.message.trim().length > 0
                                ? err.message
                                : 'Could not return to the payment page.'
                        );
                        log.error('Error returning to payment page', err);
                    });
                    return;
                }
                setIsRedirecting(true);
                runInBackground(
                    authApi
                        .completeCurrentCustomerPaymentLinkAttachment({
                            payment_request_uuid: paymentRequestUuid,
                        })
                        .then(() => navigate('/dashboard', { replace: true })),
                    (err) => {
                        setIsRedirecting(false);
                        setError(
                            err instanceof Error && err.message.trim().length > 0
                                ? err.message
                                : 'Could not attach the paid payment request to your account.'
                        );
                        log.error('Error attaching paid payment request to current session', err);
                    }
                );
                return;
            }
            runInBackground(navigate(postLoginPath, { replace: true }), (err) => {
                log.error('Error navigating after existing session check', err);
            });
        }
    }, [
        existingProfile,
        log,
        navigate,
        paymentRequestUuid,
        returnToPayment,
        postLoginPath,
        setUserForEffectiveSessionGeneration,
        user,
    ]);

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm<LoginPageFormData>({
        resolver: zodResolver(LoginPageFormSchema),
        defaultValues: {
            username: prefilledEmail,
        },
    });
    const usernameRegistration = register('username', {
        setValueAs: normalizeUserEmailInputForSubmission,
    });
    const passwordLoginSearchParams = new URLSearchParams(searchParams);
    const enteredEmail = normalizeUserEmailInputForSubmission(watch('username'));
    if (enteredEmail.length > 0) {
        passwordLoginSearchParams.set('email', enteredEmail);
    }
    const passwordLoginQuery = passwordLoginSearchParams.toString();
    const passwordLoginPath = `/login/password${
        passwordLoginQuery.length > 0 ? `?${passwordLoginQuery}` : ''
    }`;

    const loginMutation = useMutation({
        mutationFn: async (data: LoginPageFormData) => {
            return await authApi.requestPasswordlessLogin({
                email: data.username,
                ...(!returnToPayment && paymentRequestUuid.length > 0
                    ? { payment_request_uuid: paymentRequestUuid }
                    : {}),
            });
        },
        onSuccess: (_data, variables) => {
            window.sessionStorage.setItem(PASSWORDLESS_LOGIN_EMAIL_STORAGE_KEY, variables.username);
            setIsRedirecting(true);
            const query = searchParams.toString();
            runInBackground(
                navigate(`/login/passwordless${query.length > 0 ? `?${query}` : ''}`),
                (err) => {
                    setIsRedirecting(false);
                    setError('Could not open the sign-in code screen.');
                    log.error('Error navigating to passwordless code entry', err);
                }
            );
        },
        onError: (err: Error) => {
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError(`Error: ${JsonValuePrunedSchema.parse(err)}`);
            }
            log.error('Error requesting passwordless login', err);
        },
    });

    const onSubmit = (data: LoginPageFormData) => {
        const normalizedUsername: string = normalizeUserEmailInputForSubmission(data.username);
        setValue('username', normalizedUsername);
        setError('');
        loginMutation.mutate({ username: normalizedUsername });
    };

    if (error.length > 0 && (user || existingProfile)) {
        return (
            <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-purple-50">
                <Card className="w-full max-w-md">
                    <CardHeader className="space-y-1">
                        <CardTitle>Account linking unavailable</CardTitle>
                        <CardDescription>
                            We could not attach this paid payment request to your current account.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                            {error}
                        </div>
                        <Button
                            type="button"
                            className="w-full"
                            onClick={() => navigate('/dashboard', { replace: true })}
                        >
                            Continue to dashboard
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Show loading screen while checking for existing session or during redirect
    if (isCheckingSession || isRedirecting || user || existingProfile) {
        return (
            <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-purple-50">
                <Card className="w-full max-w-md">
                    <CardContent className="pt-6 pb-6">
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                            <p className="text-sm text-muted-foreground">
                                {isRedirecting ? 'Signing you in...' : 'Checking session...'}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-purple-50">
            <Card className="w-full max-w-md">
                <CardHeader className="space-y-1">
                    <CardTitle className="text-3xl font-bold text-center flex justify-center">
                        <AnchorbaseLogo width={280} />
                    </CardTitle>
                    <CardDescription className="text-center">
                        Enter your work email and we&apos;ll send you a code.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="username">Work email</Label>
                            <Input
                                id="username"
                                type="email"
                                placeholder="you@example.com"
                                autoComplete="username"
                                {...usernameRegistration}
                                onBlur={(event) => {
                                    const normalizedUsername: string =
                                        normalizeUserEmailInputForSubmission(
                                            event.currentTarget.value
                                        );
                                    event.currentTarget.value = normalizedUsername;
                                    setValue('username', normalizedUsername, {
                                        shouldDirty: true,
                                        shouldTouch: true,
                                        shouldValidate: true,
                                    });
                                    return usernameRegistration.onBlur(event);
                                }}
                                readOnly={prefilledEmail.length > 0}
                                disabled={loginMutation.isPending}
                            />
                            {errors.username && (
                                <p className="text-sm text-destructive">
                                    {errors.username.message}
                                </p>
                            )}
                        </div>

                        {error && (
                            <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                                {error}
                            </div>
                        )}

                        <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
                            {loginMutation.isPending ? 'Sending code...' : 'Send code'}
                        </Button>

                        <div className="text-center">
                            <a
                                href={passwordLoginPath}
                                className="text-sm text-primary hover:underline"
                            >
                                Log in with your password
                            </a>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
